import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Globe, Check, User, LogOut, Globe2, TrendingUp, Wrench, Sparkles, MapPin, Zap, ClipboardCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import CurrencySelector from "@/components/site/CurrencySelector";
import { getCustomerEmail, clearCustomerEmail, onCustomerChange } from "@/lib/customer";
import { EASE } from "@/components/motion/primitives";

// Mobile menu — full site map.
const MOBILE_NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Pricing", to: "/pricing" },
  { label: "Programs", to: "/clients" },
  { label: "Tools", to: "/tools" },
  { label: "Shop", to: "/shop" },
  { label: "Blog", to: "/blog" },
  { label: "Free Website Audit", to: "/free-website-audit" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const SERVICE_SUBS = [
  { slug: "web-design",     label: "Web Design & Development", desc: "Business sites, e-commerce, booking, web apps.", Icon: Globe2 },
  { slug: "seo-marketing",  label: "SEO & Search Visibility",  desc: "Get found when customers are searching.",         Icon: TrendingUp },
  { slug: "website-care",   label: "Website Care",             desc: "Hosting, security, backups — free for 30 days.", Icon: Wrench },
  { slug: "digital-growth", label: "Digital Growth",           desc: "Conversion, lead-gen, automation & AI.",          Icon: Sparkles },
];

const MEGAS = {
  services: {
    label: "Services", to: "/services", testKey: "services",
    activeMatch: (p) => p.startsWith("/services"),
    footer: "Not sure which one you need?",
  },
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [customerEmail, setEmailState] = useState(null);
  const [userMenu, setUserMenu] = useState(false);
  const [mega, setMega] = useState(null); // "websites" | "services" | "resources" | null
  const { lang, setLang, langs } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setEmailState(getCustomerEmail());
    return onCustomerChange((em) => setEmailState(em));
  }, []);

  useEffect(() => {
    setOpen(false); setUserMenu(false); setMega(null);
  }, [location.pathname]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  const simpleLink = (label, to, match, testKey) => (
    <Link
      key={to}
      to={to}
      data-testid={`nav-link-${testKey}`}
      data-i18n-skip={label === "Pricing" ? "true" : undefined}
      className={`text-sm font-medium link-underline ${match(location.pathname) ? "text-primary" : "text-foreground/80 hover:text-foreground"}`}
    >
      {label}
    </Link>
  );

  const megaMenu = (key) => {
    const m = MEGAS[key];
    const isOpen = mega === key;
    return (
      <div key={key} className="relative" onMouseEnter={() => setMega(key)} onMouseLeave={() => setMega(null)} data-testid={`nav-${key}-wrapper`}>
        <Link
          to={m.to}
          data-testid={`nav-link-${key}`}
          onFocus={() => setMega(key)}
          className={`text-sm font-medium link-underline ${m.activeMatch(location.pathname) ? "text-primary" : "text-foreground/80 hover:text-foreground"}`}
        >
          {m.label}
        </Link>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.18, ease: EASE }}
              className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[720px] z-[110]"
              data-testid={`${key}-mega-menu`}
            >
              <div className="border border-border bg-background shadow-2xl max-h-[70vh] overflow-y-auto">
                {key === "services" && (
                  <div className="grid grid-cols-2 gap-1 p-4">
                    {SERVICE_SUBS.map(({ slug, label, desc, Icon }) => (
                      <Link key={slug} to={`/services/${slug}`} data-testid={`nav-service-${slug}`}
                        className="group flex items-start gap-4 p-4 hover:bg-secondary transition-colors">
                        <div className="w-10 h-10 grid place-items-center rounded-lg bg-primary/10 text-primary flex-shrink-0"><Icon className="w-5 h-5" /></div>
                        <div className="min-w-0">
                          <p className="font-display text-lg leading-tight group-hover:text-primary transition-colors">{label}</p>
                          <p className="text-xs text-muted-foreground mt-1 leading-snug">{desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-secondary/50 text-sm">
                  <span className="text-muted-foreground">{m.footer}</span>
                  <Link to={m.to} data-testid={`nav-${key}-view-all`} className="inline-flex items-center gap-1.5 text-primary font-medium hover:gap-2 transition-all">
                    View all <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
          scrolled || open ? "bg-background border-b border-border" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="font-display text-xl md:text-2xl tracking-tight leading-none">
            JULIUS<span className="text-primary">.</span>MASON
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {megaMenu("services")}
            {simpleLink("Pricing", "/pricing", (p) => p === "/pricing", "pricing")}
            {simpleLink("Programs", "/clients", (p) => p.startsWith("/clients"), "clients")}
            {simpleLink("Tools", "/tools", (p) => p.startsWith("/tools"), "tools")}
            {simpleLink("Shop", "/shop", (p) => p === "/shop", "shop")}
            {simpleLink("Blog", "/blog", (p) => p.startsWith("/blog"), "blog")}
            {simpleLink("Contact", "/contact", (p) => p === "/contact", "contact")}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <CurrencySelector />
            <div className="relative">
              <button
                data-testid="lang-toggle"
                onClick={() => setLangOpen((o) => !o)}
                className="flex items-center gap-1.5 p-2 hover:text-primary transition-colors text-sm font-mono uppercase"
                aria-label="Language"
              >
                <Globe className="w-5 h-5" /> <span className="hidden sm:inline">{lang}</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                    data-testid="lang-menu"
                    className="absolute right-0 mt-2 w-48 max-h-80 overflow-y-auto bg-card border border-border shadow-2xl py-1 z-[120]"
                  >
                    {langs.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`lang-${l.code}`}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-secondary transition-colors ${lang === l.code ? "text-primary" : "text-foreground/80"}`}
                      >
                        {l.name} {lang === l.code && <Check className="w-3.5 h-3.5" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {customerEmail && (
              <div className="relative">
                <button
                  data-testid="nav-account-chip"
                  onClick={() => setUserMenu((o) => !o)}
                  className="hidden md:inline-flex items-center gap-1.5 p-2 hover:text-primary transition-colors"
                  aria-label="Account"
                  title={customerEmail}
                >
                  <User className="w-5 h-5" />
                </button>
                <AnimatePresence>
                  {userMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      data-testid="nav-account-menu"
                      className="absolute right-0 mt-2 w-64 bg-card border border-border shadow-2xl py-2"
                    >
                      <div className="px-4 py-2 border-b border-border">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-0.5">Signed in as</p>
                        <p className="text-sm font-medium truncate" data-testid="nav-account-email">{customerEmail}</p>
                      </div>
                      <button onClick={() => { clearCustomerEmail(); setUserMenu(false); }} data-testid="nav-account-signout"
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-secondary transition">
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            <button
              data-testid="nav-quote-cta"
              onClick={() => navigate("/pricing")}
              className="hidden md:inline-flex items-center gap-1.5 bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors active:scale-95"
            >
              Get Instant Quote <Zap className="w-4 h-4" />
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="p-2 hover:text-primary transition-colors lg:hidden"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="fullscreen-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[90] bg-background pt-[72px] overflow-y-auto"
          >
            <div className="mx-auto max-w-[1600px] px-5 md:px-10 min-h-full flex flex-col justify-center py-10">
              <div className="grid md:grid-cols-2 gap-y-1 md:gap-y-2">
                {MOBILE_NAV.map((n, i) => (
                  <motion.div
                    key={n.to}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.03, ease: EASE, duration: 0.5 }}
                  >
                    <Link
                      to={n.to}
                      data-i18n-skip={n.label === "Pricing" ? "true" : undefined}
                      data-testid={`menu-link-${n.label.toLowerCase().replace(/ /g, "-")}`}
                      className="group flex items-center gap-4 py-2 md:py-3 border-b border-border/50"
                    >
                      <span className="font-display text-3xl md:text-4xl leading-none group-hover:text-primary group-hover:translate-x-2 transition-all duration-300">
                        {n.label}
                      </span>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 text-primary transition-opacity ml-auto" />
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/pricing" data-testid="menu-quote-cta"
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3.5 font-semibold">
                  Get Instant Quote <Zap className="w-4 h-4" />
                </Link>
                <Link to="/free-website-audit" data-testid="menu-audit-cta"
                  className="inline-flex items-center gap-2 border border-border px-6 py-3.5 font-semibold hover:border-primary hover:text-primary">
                  <ClipboardCheck className="w-4 h-4" /> Free Website Audit
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 font-mono text-xs text-muted-foreground uppercase tracking-widest">
                <a href="mailto:julius.a.mason@gmail.com" className="hover:text-primary">julius.a.mason@gmail.com</a>
                <span className="text-primary flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Based in France · Serving businesses worldwide</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
